import Cookies from 'js-cookie';

import { getStoreData } from './api';
import {
  LoginCredentials,
  StoreResponse,
  CategoryResponse,
  ProductResponse,
  UserLoginCredentials,
  UserAuthResponse,
  StoreDataResponse,
} from '../types';

const API_BASE_URL = 'https://api.restomi.fr/v3/api';

// Fonction pour journaliser les appels API
const logApiCall = (
  endpoint: string,
  request: any,
  response: any,
  status: number
) => {
  // Logs désactivés pour éviter les erreurs de quota
};
interface StoreInfo {
  store_id: string;
  server_token: string;
  address: string;
  location: [number, number];
  email: string;
  phone: string;
  name: string;
  image_url: string;
  country_phone_code: string;
  is_tax_included: boolean;
  item_tax: number;
  is_use_item_tax: boolean;
  general_tax: number;
}

let storeInfo: StoreInfo | null = null;

const handleResponse = async <T>(response: Response): Promise<T> => {
  const data = await response.json();
  logApiCall(API_BASE_URL, {}, data, response.status);
  if (!response.ok) {
    throw new Error(
      `HTTP error! status: ${response.status}, message: ${
        data.status_phrase || data.message
      }`
    );
  }
  if (!data.success) {
    throw new Error(`API error: ${data.status_phrase || data.message}`);
  }
  return data;
};

export async function loginStore(
  credentials: LoginCredentials
): Promise<StoreResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/store/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    const data = await handleResponse<StoreResponse>(response);

    console.log(
      'Réponse complète de /store/login:',
      JSON.stringify(data, null, 2)
    );

    if (data.success && data.store) {
      storeInfo = {
        store_id: data.store._id,
        server_token: data.store.server_token,
        address: data.store.address || '123 Rue Exemple, Paris, France',
        location: [
          data.store.location?.[0] || 48.8566,
          data.store.location?.[1] || 2.3522,
        ],
        email: data.store.email || 'contact@restaurant.com',
        phone: data.store.phone || '0123456789',
        name: data.store.name?.[0] || 'Restaurant Exemple',
        image_url: data.store.image_url || '',
        country_phone_code: data.store.country_phone_code || '+33',
        is_tax_included: data.store.is_tax_included ?? true,
        item_tax: data.store.item_tax ?? 0,
        is_use_item_tax: data.store.is_use_item_tax ?? true,
        general_tax: 20,
      };

      localStorage.setItem('storeInfo', JSON.stringify(storeInfo));
      console.log('storeInfo sauvegardé après login:', storeInfo);
    } else {
      console.warn('Login réussi mais aucune donnée de store reçue:', data);
    }

    return data;
  } catch (error) {
    console.error(
      'Login error:',
      error instanceof Error ? error.message : 'Unknown error'
    );
    logApiCall(
      `${API_BASE_URL}/store/login`,
      credentials,
      { error: error instanceof Error ? error.message : 'Unknown error' },
      500
    );
    throw error;
  }
}

export function getStoreInfo(): StoreInfo {
  if (!storeInfo) {
    const savedInfo = localStorage.getItem('storeInfo');
    if (savedInfo) {
      try {
        const parsed = JSON.parse(savedInfo);
        if (
          parsed &&
          typeof parsed === 'object' &&
          'store_id' in parsed &&
          'server_token' in parsed &&
          'address' in parsed &&
          'location' in parsed &&
          'email' in parsed &&
          'phone' in parsed &&
          'name' in parsed &&
          'image_url' in parsed &&
          'country_phone_code' in parsed &&
          'is_tax_included' in parsed &&
          'is_use_item_tax' in parsed
        ) {
          storeInfo = {
            ...parsed,
            general_tax: parsed.general_tax ?? 20,
          };
          console.log('storeInfo récupéré de localStorage:', storeInfo);
        } else {
          localStorage.removeItem('storeInfo');
          console.warn('Données invalides dans localStorage, supprimées');
        }
      } catch (e) {
        console.error('Erreur parsing storeInfo:', e);
        localStorage.removeItem('storeInfo');
      }
    }
  }
  if (!storeInfo) {
    storeInfo = {
      store_id: 'default-store-id',
      server_token: 'default-server-token',
      address: '123 Rue Exemple, Paris, France',
      location: [48.8566, 2.3522],
      email: 'contact@restaurant.com',
      phone: '0123456789',
      name: 'Restaurant Exemple',
      image_url: '',
      country_phone_code: '+33',
      is_tax_included: true,
      item_tax: 0,
      is_use_item_tax: true,
      general_tax: 20,
    };
    console.log('storeInfo par défaut:', storeInfo);
  }
  return storeInfo;
}
export async function logoutStore(
  storeId: string,
  serverToken: string
): Promise<void> {
  try {
    const response = await fetch(`${API_BASE_URL}/store/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'server-token': serverToken,
      },
      body: JSON.stringify({
        store_id: storeId,
        server_token: serverToken,
      }),
    });

    await handleResponse(response);
  } catch (error) {
    console.warn(
      'Logout API call failed:',
      error instanceof Error ? error.message : 'Unknown error'
    );
    logApiCall(
      `${API_BASE_URL}/store/logout`,
      { store_id: storeId, server_token: serverToken },
      { error: error.message },
      500
    );
  } finally {
    Cookies.remove('storeId');
    Cookies.remove('serverToken');
    console.log('Cookies effacés');
  }
}

export async function getCategories(
  storeId: string,
  serverToken: string
): Promise<CategoryResponse> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    const requestBody = {
      store_id: storeId,
      server_token: serverToken,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    const response = await fetch(
      `${API_BASE_URL}/store/get_product_group_list`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'server-token': serverToken,
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      }
    );

    clearTimeout(timeoutId);

    const data = await handleResponse<CategoryResponse>(response);
    if (!data.product_groups || !Array.isArray(data.product_groups)) {
      throw new Error('Invalid categories data structure');
    }
    return data;
  } catch (error) {
    console.error(
      'Get categories error:',
      error instanceof Error ? error.message : 'Unknown error'
    );
    logApiCall(
      `${API_BASE_URL}/store/get_product_group_list`,
      { store_id: storeId, server_token: serverToken },
      { error: error.message },
      500
    );
    throw error;
  }
}

export async function getProducts(
  storeId: string,
  serverToken: string,
  productId: string
): Promise<ProductResponse> {
  try {
    const requestBody = {
      store_id: storeId,
      server_token: serverToken,
      timezone: 'Europe/Paris',
      product_id: productId,
    };

    const response = await fetch(
      `${API_BASE_URL}/store/get_store_product_item_list`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'server-token': serverToken,
        },
        body: JSON.stringify(requestBody),
      }
    );

    const data = await handleResponse<ProductResponse>(response);
    if (!data.products || !Array.isArray(data.products)) {
      throw new Error('Invalid products data structure');
    }
    return data;
  } catch (error) {
    console.error(
      'Get products error:',
      error instanceof Error ? error.message : 'Unknown error'
    );
    logApiCall(
      `${API_BASE_URL}/store/get_store_product_item_list`,
      { store_id: storeId, server_token: serverToken, product_id: productId },
      { error: error.message },
      500
    );
    throw error;
  }
}

export async function createOrder(
  storeId: string,
  serverToken: string,
  order: {
    items: Array<{
      menuItemId: string;
      quantity: number;
      customizations: {
        [key: string]: Array<{ id: string; name: string; price: number }>;
      };
      price: number;
    }>;
    total: number;
    orderType: 'dine-in' | 'takeaway';
    paymentType: 'card' | 'cash';
    createdAt: string;
  }
): Promise<{ success: boolean; orderId: string }> {
  try {
    const requestBody = {
      store_id: storeId,
      server_token: serverToken,
      order_details: order.items.map((item) => ({
        item_id: item.menuItemId,
        quantity: item.quantity,
        specifications: item.customizations,
        price: item.price,
      })),
      total_amount: order.total,
      order_type: order.orderType,
      payment_type: order.paymentType,
      created_at: order.createdAt,
    };

    const response = await fetch(`${API_BASE_URL}/store/create_order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'server-token': serverToken,
      },
      body: JSON.stringify(requestBody),
    });

    const data = await handleResponse<{ success: boolean; order_id: string }>(
      response
    );
    return { success: data.success, orderId: data.order_id };
  } catch (error) {
    console.error(
      'Create order error:',
      error instanceof Error ? error.message : 'Unknown error'
    );
    logApiCall(
      `${API_BASE_URL}/store/create_order`,
      { store_id: storeId, ...order },
      { error: error.message },
      500
    );
    throw error;
  }
}

export async function loginUser(
  credentials: UserLoginCredentials
): Promise<UserAuthResponse> {
  try {
    const requestBody = {
      email: credentials.email,
      password: credentials.password,
      login_by: credentials.login_by,
      social_id: credentials.social_id,
      device_token: credentials.device_token,
      device_type: credentials.device_type,
      app_version: credentials.app_version,
      store_type: credentials.store_type,
    };

    const response = await fetch(`${API_BASE_URL}/user/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    return await handleResponse<UserAuthResponse>(response);
  } catch (error) {
    console.error(
      'User login error:',
      error instanceof Error ? error.message : 'Unknown error'
    );
    logApiCall(
      `${API_BASE_URL}/user/login`,
      credentials,
      { error: error.message },
      500
    );
    throw error;
  }
}

/*
export async function getStoreData(
  storeId: string,
  serverToken: string
): Promise<StoreDataResponse> {
  try {
    const requestBody = {
      store_id: storeId,
      servertoken: serverToken,
    };

    const response = await fetch(`${API_BASE_URL}/store/get_store_data`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'server-token': serverToken,
      },
      body: JSON.stringify(requestBody),
    });
 
    const data = await handleResponse<StoreDataResponse>(response);
    return data;
  } catch (error) {
    console.error(
      'Get store data error:',
      error instanceof Error ? error.message : 'Unknown error'
    );
    logApiCall(
      `${API_BASE_URL}/store/get_store_data`,
      { store_id: storeId, servertoken: serverToken },
      { error: error.message },
      500
    );
    throw error;
  }
}
*/
